import { useState, useEffect, useCallback, ReactNode } from "react";

const SESSION_KEY = "uis_auth";
const PASSWORD_ENV = process.env.REACT_APP_PASSWORD ?? "";

function isAuthenticated(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function setAuthenticated(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {}
}

interface Props {
  children: ReactNode;
}

export function PasswordGate({ children }: Props) {
  const [unlocked, setUnlocked] = useState(isAuthenticated);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const misconfigured = PASSWORD_ENV.length === 0;

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!password) return;

      if (password === PASSWORD_ENV) {
        setAuthenticated();
        setUnlocked(true);
      } else {
        setError(true);
        setPassword("");
        triggerShake();
      }
    },
    [password, triggerShake]
  );

  useEffect(() => {
    if (unlocked) return;
    const onFocus = () => {
      if (!isAuthenticated()) setUnlocked(false);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [unlocked]);

  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-blue-700/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-violet-700/20 rounded-full blur-3xl" />
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-sm mx-4"
        style={
          shake
            ? { animation: "shake 0.5s cubic-bezier(.36,.07,.19,.97) both" }
            : {}
        }
      >
        <div className="text-center mb-8">
          <p className="text-blue-400 text-xs font-bold tracking-[0.35em] uppercase mb-3">
            ✦ Component Library
          </p>
          <h1 className="text-4xl font-black text-white tracking-tight">
            Route{" "}
            <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
              Explorer
            </span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">
            Enter the access password to continue
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {misconfigured ? (
            <p className="text-red-400 text-sm text-center">
              Misconfiguration: REACT_APP_PASSWORD is not set in .env
            </p>
          ) : (
            <>
              <label className="block text-gray-400 text-xs font-semibold uppercase tracking-widest mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                placeholder="••••••••••"
                autoFocus
                autoComplete="current-password"
                className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-white placeholder-gray-600 outline-none focus:ring-2 transition-all duration-200 ${
                  error
                    ? "border-red-500/60 focus:ring-red-500/30"
                    : "border-white/10 focus:ring-blue-500/40 focus:border-blue-500/40"
                }`}
              />

              {error && (
                <p className="text-red-400 text-xs mt-2">
                  Incorrect password. Try again.
                </p>
              )}

              <button
                type="submit"
                disabled={!password}
                className="mt-6 w-full py-3 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-all duration-200 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
              >
                Unlock
              </button>
            </>
          )}
        </div>
      </form>

      <style>{`
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(4px); }
          30%, 50%, 70% { transform: translateX(-6px); }
          40%, 60% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
